//third party
import { Duration, LocalDateTime, LocalTime, Period } from "@js-joda/core";
import { dinero as createMoney, Dinero, add, lessThan } from 'dinero.js';
import { USD } from '@dinero.js/currencies';

//spotsync
import { cents } from "./Money";
import { gt, lte, max, min } from "./Duration";
import { RateProgram as RateProgram } from "./ApiTypes";

export function findReset( prog: RateProgram ) {
    return prog.entries.map( r => r.upTo ).reduce( (a, b) => max( a, b ), prog.entries[0].upTo );
}

function GetPeriod( a: LocalTime ): Duration {
    const h = a.hour();
    const m = a.minute();
    const s = a.second();
    const diff = Duration.ofHours( h ).plusMinutes( m ).plusSeconds( s );
    return diff;
}

function Between(t: LocalTime, a: LocalTime, b: LocalTime): boolean {
    if (a == b)
        return true;
    const diff = GetPeriod(a);
    const an = LocalTime.MIDNIGHT;
    const bn = b.minus( diff );
    const tn = t.minus( diff );
    return tn > an && tn < bn;
}

export function getNonEventRates(allRates:RateProgram[]) : RateProgram[]{
  return allRates.filter(r => !r.isEvent && !r.isValidation)
}

export function computeCharge(
  programs: RateProgram[],
  a: LocalDateTime,
  b: LocalDateTime
) {
  let lowestFee = createMoney({ amount: Number.MAX_SAFE_INTEGER, currency: USD, scale: 2 });

  for (const prog of programs) {
    if (!isProgramApplicable(prog, a, b)) {
      continue;
    }

    let currentFee = createMoney({ amount: 0, currency: USD, scale: 2 });

    if (prog.isEvent) {
      const rate = prog.entries[0].rate as Dinero<number>;
      currentFee = rate;
    } else {
      const duration = Duration.between(a, b);
      const entries = prog.entries;
      const chunk = duration;

      const minEntry = entries.find(entry => chunk.compareTo(entry.upTo) <= 0);
      if (minEntry) {
        const rate = minEntry.rate as Dinero<number>;
        currentFee = rate;
      }
    }

    if (lessThan(currentFee, lowestFee) && currentFee.toJSON().amount > 0) {
      lowestFee = currentFee;
    }
  }

  return lowestFee;
}


function isProgramApplicable(prog: RateProgram, a: LocalDateTime, b: LocalDateTime): boolean {
    const inTime = a.toLocalTime();
    const outTime = b.toLocalTime();
  
    return isWithinTimeRange(inTime, prog.inAfter, prog.inBefore) &&
           isWithinTimeRange(outTime, prog.outAfter, prog.outBefore);
}

function isWithinTimeRange(
  time: LocalTime,
  start: LocalTime | null,
  end: LocalTime | null
): boolean {
  if (!start && !end) {
    return true; 
  }

  if (start && end) {
    if (end.isAfter(start)) {
      return !time.isBefore(start) && !time.isAfter(end);
    } else {
      return !time.isBefore(start) || !time.isAfter(end);
    }
  }

  if (start) {
    return !time.isBefore(start);
  }

  if (end) {
    if (end.isBefore(LocalTime.NOON)) {
    // If only an early morning 'end' time is set (no 'start'), treat as a cross-midnight range:
    // allow times after 17:00 (5 PM) or before the 'end' time (e.g., 05:00).
      return time.isBefore(end) || time.isAfter(LocalTime.of(17, 0)); 
    }
    return !time.isAfter(end);
  }

  return true;
}